import { snakeCase } from 'change-case';
import { SortDirection } from '@tanstack/react-table';

import { RequestConfig } from '~/common/services/requests';
import { EMPTY_OBJ } from '../constants';
import { DocumentRelationshipObject } from '../types';
import { Pagination, Sorting } from '../types/utility';
import { camelToSnakeCase } from './general';

export const createSortFilter = (
  sort: Sorting,
): {
  sortcolumn: string;
  sortdirection: SortDirection;
} => ({
  sortcolumn: camelToSnakeCase(sort.column),
  sortdirection: sort.direction,
});

const shouldBeIgnored = (
  value: string | number | boolean | undefined | Array<string | number>,
): boolean =>
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

export const createFilterParams = <
  T extends Record<string, string | number | boolean> = Record<
    string,
    string | number | boolean
  >,
>(
  filters: Record<
    string,
    string | number | boolean | undefined | Array<string | number>
  >,
): T => {
  const result = Object.entries(filters).reduce<
    Record<string, string | number | boolean>
  >((acc, [k, v]) => {
    if (shouldBeIgnored(v)) return acc;
    const base = `filter[${camelToSnakeCase(k)}]`;

    if (Array.isArray(v) && v.length > 1) {
      // expand to base[0], base[1], ...
      v.forEach((value, index) => {
        acc[`${base}[${index}]`] = value ?? '';
      });
    } else {
      // keep as a single key
      acc[base] = (Array.isArray(v) ? v[0] : v) ?? '';
    }

    return acc;
  }, {});
  return result as T;
};

export const createParams = <
  T extends Record<string, string | number | boolean> = Record<
    string,
    string | number | boolean
  >,
>(
  filters: Record<
    string,
    string | number | boolean | undefined | Array<string | number>
  >,
): T => {
  const result = Object.entries(filters).reduce<
    Record<string, string | number | boolean>
  >((acc, [k, v]) => {
    if (shouldBeIgnored(v)) return acc;
    const base = camelToSnakeCase(k);

    if (Array.isArray(v) && v.length > 1) {
      // expand to base[0], base[1], ...
      v.forEach((value, index) => {
        acc[`${base}[${index}]`] = value ?? '';
      });
    } else {
      // keep as a single key
      acc[base] = (Array.isArray(v) ? v[0] : v) ?? '';
    }

    return acc;
  }, {});
  return result as T;
};

export const createSearchParams = (
  filter:
    | Record<
        string,
        string | number | boolean | undefined | Array<string | number>
      >
    | undefined,
  pagination: Pagination | undefined,
  sorting: Sorting | undefined,
): RequestConfig['params'] => ({
  ...(filter ? createFilterParams(filter) : EMPTY_OBJ),
  ...(pagination || EMPTY_OBJ),
  ...(sorting ? createSortFilter(sorting) : EMPTY_OBJ),
});

export const getDocumentIncludesForRelationshipObject = (
  relationshipsObject: Record<string, DocumentRelationshipObject>,
  keyPrefixes: string[] = [],
): string[] =>
  Object.entries(relationshipsObject).reduce<string[]>(
    (array, [key, { relationships }]) => {
      const compositeKey = [...keyPrefixes, snakeCase(key)];
      return [
        ...array,
        compositeKey.join('.'),
        ...(relationships
          ? getDocumentIncludesForRelationshipObject(
              relationships,
              compositeKey,
            )
          : []),
      ];
    },
    [],
  );
